import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Film, Copy, Save, RotateCcw, Eye, Users, Mail } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { XaltixStats, PeriodPicker } from "./XaltixStats";

const NUM = { planning: "01", devis: "02", conformite: "03", agent: "04", dashboard: "05", candidats: "06", gps: "07", alertes: "08" };
const LINK = (src) => `https://solutions-prime.fr/xaltix?src=${src}`;
const ZERO = { visits: 0, uniques: 0, contacts: 0 };
const PERIOD_LABEL = { 7: "7 jours", 30: "30 jours", 90: "90 jours", 0: "depuis le début" };

const Stats = ({ s, testid }) => (
  <div data-testid={testid} className="flex gap-3 text-[11px] text-slate-600">
    <span className="inline-flex items-center gap-1" title="Visites"><Eye className="h-3 w-3" />{s.visits}</span>
    <span className="inline-flex items-center gap-1" title="Visiteurs uniques (par jour)"><Users className="h-3 w-3" />{s.uniques}</span>
    <span className="inline-flex items-center gap-1" title="Contacts obtenus"><Mail className="h-3 w-3" />{s.contacts}</span>
  </div>
);

const Format = ({ k, story, stats, onCopy }) => {
  const file = `/xaltix-linkedin/xaltix-${NUM[k]}-${k}${story ? "-story" : ""}`;
  const src = `li-${k}${story ? "-story" : ""}`;
  return (
    <div className="space-y-1.5">
      <video src={`${file}.mp4?v=5`} poster={`${file}.jpg?v=5`} controls muted playsInline preload="none" className={`w-full rounded-md bg-slate-900 ${story ? "aspect-[9/16]" : "aspect-square"}`} />
      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{story ? "Story 9:16" : "Fil 1:1"}</div>
      <Stats s={stats[src] || ZERO} testid={`stats-${src}`} />
      <div className="flex flex-wrap gap-2">
        <a data-testid={`linkedin-download-${src}`} href={`${file}.mp4`} download={`XALTIX-${k}${story ? "-story" : ""}.mp4`} className="inline-flex items-center gap-1 text-xs font-bold text-sky-700"><Download className="h-3.5 w-3.5" /> Vidéo</a>
        <button data-testid={`linkedin-copy-${src}`} onClick={() => onCopy(src)} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><Copy className="h-3.5 w-3.5" /> Copier le texte</button>
      </div>
    </div>
  );
};

const VideoCard = ({ p, stats }) => {
  const [text, setText] = useState(p.text);
  const copy = async (src) => {
    try {
      await navigator.clipboard.writeText(`${text.trim()}\n\n→ ${LINK(src)}`);
      toast.success("Texte copié avec le lien suivi");
    } catch { toast.error("Copie impossible : autorisez l'accès au presse-papiers"); }
  };
  const save = async () => { try { await http.put(`/xaltix/posts/${p.key}`, { text }); toast.success("Texte enregistré"); } catch (e) { toast.error(formatApiError(e)); } };
  const tot = ["", "-story"].map((x) => stats[`li-${p.key}${x}`] || ZERO).reduce((a, b) => ({ visits: a.visits + b.visits, uniques: a.uniques + b.uniques, contacts: a.contacts + b.contacts }), ZERO);
  return (
    <div data-testid={`linkedin-video-${p.key}`} className="border border-slate-200 rounded-lg p-4 grid md:grid-cols-[1fr_0.56fr_1.4fr] gap-4">
      <Format k={p.key} stats={stats} onCopy={copy} />
      <Format k={p.key} story stats={stats} onCopy={copy} />
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <div className="font-bold">{NUM[p.key]} · {p.label}</div>
          <span data-testid={`linkedin-visits-${p.key}`} className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">{tot.visits} visite{tot.visits > 1 ? "s" : ""} · {tot.contacts} contact{tot.contacts > 1 ? "s" : ""}</span>
        </div>
        <textarea data-testid={`linkedin-text-${p.key}`} value={text} onChange={(e) => setText(e.target.value)} rows={10} className="mt-2 flex-1 w-full border border-slate-300 rounded-md px-2.5 py-2 text-sm" />
        <div className="text-[11px] text-slate-400 mt-1">Lien suivi ajouté à la copie : ?src=li-{p.key} (fil) ou ?src=li-{p.key}-story (story)</div>
        <div className="flex gap-2 mt-2">
          <button data-testid={`linkedin-save-${p.key}`} onClick={save} className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-1.5 rounded-md text-xs font-semibold"><Save className="h-3.5 w-3.5" /> Enregistrer</button>
          <button data-testid={`linkedin-reset-${p.key}`} onClick={() => { setText(p.default); toast.info("Texte par défaut rétabli : cliquez sur Enregistrer pour le conserver"); }} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 rounded-md text-xs font-semibold"><RotateCcw className="h-3.5 w-3.5" /> Texte par défaut</button>
        </div>
      </div>
    </div>
  );
};

export const LinkedinVideos = () => {
  const [posts, setPosts] = useState(null);
  const [stats, setStats] = useState({});
  const [overview, setOverview] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { http.get("/xaltix/posts").then((r) => setPosts(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => {
    http.get("/xaltix/stats", { params: { days } }).then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
    http.get("/xaltix/overview", { params: { days } }).then((r) => setOverview(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [days]);
  const direct = stats.direct || ZERO;
  return (
    <div className="space-y-4">
    <div data-testid="xaltix-stats-panel" className="border border-slate-200 rounded-lg p-5 bg-white space-y-4">
      <div className="flex flex-wrap items-center gap-2"><h2 className="font-bold">Statistiques de la page XALTIX</h2><div className="ml-auto"><PeriodPicker days={days} setDays={setDays} /></div></div>
      <XaltixStats overview={overview} posts={posts} stats={stats} />
    </div>
    <div data-testid="linkedin-videos" className="border border-slate-200 rounded-lg p-5 bg-white space-y-4">
      <div className="flex flex-wrap items-center gap-2"><Film className="h-4 w-4" /><h2 className="font-bold">Vidéos LinkedIn</h2>
        <span data-testid="stats-direct" className="ml-auto text-xs text-slate-500">Visites directes (sans lien suivi) : {direct.visits} · {direct.contacts} contact(s) · {PERIOD_LABEL[days]}</span></div>
      <p className="text-xs text-slate-500 -mt-2">15 s, sans son, données fictives. Compteurs sans cookie : visites, visiteurs uniques par jour et contacts obtenus par lien.</p>
      {posts?.map((p) => <VideoCard key={p.key} p={p} stats={stats} />)}
      <div data-testid="linkedin-video-demo" className="flex items-center gap-3 text-sm">
        <span className="font-semibold">Démo complète (1 min, 16:9)</span>
        <a data-testid="linkedin-download-demo" href="/xaltix-demo.mp4" download="XALTIX-demo.mp4" className="inline-flex items-center gap-1 text-xs font-bold text-sky-700"><Download className="h-3.5 w-3.5" /> Télécharger</a>
      </div>
    </div>
    </div>
  );
};
